import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export type PatientMedicalTextExampleInsert = Database["public"]["Tables"]["patient_medical_text_examples"]["Insert"];
export type EncounterMedicalTextExampleInsert =
  Database["public"]["Tables"]["patient_encounter_medical_text_examples"]["Insert"];

export type MedicalTextAssociation = {
  medical_text_example_id: PatientMedicalTextExampleInsert["medical_text_example_id"] &
    EncounterMedicalTextExampleInsert["medical_text_example_id"];
  patient_id?: PatientMedicalTextExampleInsert["patient_id"];
  patient_encounter_id?: EncounterMedicalTextExampleInsert["patient_encounter_id"];
};

export type InsertPatientAssociationBody = Pick<
  PatientMedicalTextExampleInsert,
  "patient_id" | "medical_text_example_id"
>;

export type InsertEncounterAssociationBody = Pick<
  EncounterMedicalTextExampleInsert,
  "patient_encounter_id" | "medical_text_example_id"
>;

export const useMedicalTextAssociations = (medicalTextExampleId?: string) => {
  const queryResult = useQuery({
    queryKey: ["medical-text-associations", medicalTextExampleId],
    queryFn: async () => {
      if (!medicalTextExampleId) return null;

      const [patientResult, encounterResult] = await Promise.all([
        supabase
          .from("patient_medical_text_examples")
          .select("patient_id, patients!inner(*)")
          .eq("medical_text_example_id", medicalTextExampleId)
          .maybeSingle(),
        supabase
          .from("patient_encounter_medical_text_examples")
          .select("patient_encounter_id, patient_encounters!inner(*)")
          .eq("medical_text_example_id", medicalTextExampleId)
          .maybeSingle(),
      ]);

      const patient = patientResult.data
        ? {
            ...patientResult.data.patients,
            id: patientResult.data.patient_id,
          }
        : null;

      const encounter = encounterResult.data
        ? {
            ...encounterResult.data.patient_encounters,
            id: encounterResult.data.patient_encounter_id,
          }
        : null;

      const customerId = encounter?.customer_id || patient?.customer_id || null;

      return {
        patient,
        encounter,
        customer: customerId ? { id: customerId } : null,
      };
    },
    enabled: !!medicalTextExampleId,
  });

  return queryResult;
};

export const useCreateMedicalTextAssociations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (association: MedicalTextAssociation) => {
      let patientAssociation: PostgrestSingleResponse<
        Database["public"]["Tables"]["patient_medical_text_examples"]["Row"]
      > | null = null;
      if (association.patient_id) {
        patientAssociation = await supabase
          .from("patient_medical_text_examples")
          .insert({
            patient_id: association.patient_id,
            medical_text_example_id: association.medical_text_example_id,
          })
          .select()
          .single();
      }

      let encounterAssociation: PostgrestSingleResponse<
        Database["public"]["Tables"]["patient_encounter_medical_text_examples"]["Row"]
      > | null = null;
      if (association.patient_encounter_id) {
        encounterAssociation = await supabase
          .from("patient_encounter_medical_text_examples")
          .insert({
            patient_encounter_id: association.patient_encounter_id,
            medical_text_example_id: association.medical_text_example_id,
          })
          .select()
          .single();
      }

      if (patientAssociation?.error) throw patientAssociation.error;
      if (encounterAssociation?.error) throw encounterAssociation.error;

      return {
        patientAssociation: patientAssociation?.data,
        encounterAssociation: encounterAssociation?.data,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["medical-text-associations"],
      });
      toast.success("Medical text associations created successfully");
    },
    onError: (error) => {
      console.error("Error creating medical text associations:", error);
      toast.error("Failed to create medical text associations");
    },
  });
};

export const useUpdateMedicalTextAssociations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (association: MedicalTextAssociation) => {
      let patientAssociation: PostgrestSingleResponse<
        Database["public"]["Tables"]["patient_medical_text_examples"]["Row"]
      > | null = null;
      if (association.patient_id) {
        // Try to update first
        patientAssociation = await supabase
          .from("patient_medical_text_examples")
          .update({
            patient_id: association.patient_id,
          })
          .eq("medical_text_example_id", association.medical_text_example_id)
          .select()
          .single();

        // If update failed because no rows exist (PGRST116), create the association instead
        if (patientAssociation?.error?.code === "PGRST116") {
          patientAssociation = await supabase
            .from("patient_medical_text_examples")
            .insert({
              patient_id: association.patient_id,
              medical_text_example_id: association.medical_text_example_id,
            })
            .select()
            .single();
        }
      }

      let encounterAssociation: PostgrestSingleResponse<
        Database["public"]["Tables"]["patient_encounter_medical_text_examples"]["Row"]
      > | null = null;
      if (association.patient_encounter_id) {
        // Try to update first
        encounterAssociation = await supabase
          .from("patient_encounter_medical_text_examples")
          .update({
            patient_encounter_id: association.patient_encounter_id,
          })
          .eq("medical_text_example_id", association.medical_text_example_id)
          .select()
          .single();

        // If update failed because no rows exist (PGRST116), create the association instead
        if (encounterAssociation?.error?.code === "PGRST116") {
          encounterAssociation = await supabase
            .from("patient_encounter_medical_text_examples")
            .insert({
              patient_encounter_id: association.patient_encounter_id,
              medical_text_example_id: association.medical_text_example_id,
            })
            .select()
            .single();
        }
      }

      if (patientAssociation?.error) throw patientAssociation.error;
      if (encounterAssociation?.error) throw encounterAssociation.error;

      return {
        patientAssociation: patientAssociation?.data,
        encounterAssociation: encounterAssociation?.data,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["medical-text-associations"],
      });
      queryClient.invalidateQueries({
        queryKey: ["medical-text-examples"],
      });
      toast.success("Medical text associations updated successfully");
    },
    onError: (error) => {
      console.error("Error updating medical text associations:", error);
      toast.error("Failed to update medical text associations");
    },
  });
};
